import _get from 'lodash/get';
import React, { forwardRef, Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { Form as FormikForm, Formik, FormikValues, FormikProps } from 'formik';
import * as Schema from './types/schema';
import { isEmpty } from './helpers';
import Form, { FormProps, FormViewerProps } from './components/Form/Form';
import { ChangesWrapper } from './app/components/ChangesWrapper';
import AppProvider, { AppContext } from './app/AppProvider';
import LocaleProvider from './app/LocaleProvider';

export type FormikReturnProps = FormikProps<any>;

export function FormViewer(props: FormViewerProps) {
	return (
		<AppProvider>
			<FormViewerInner {...props} />
		</AppProvider>
	);
}

interface FormViewerReccomendedProps {
	fields: Schema.Field[];
	onCancel: () => void;
	onConfirm: () => void;
}

function FormViewerReccomended(props: FormViewerReccomendedProps) {
	const { fields, onConfirm, onCancel } = props;
	const [show, setShow] = useState(false);

	const handleClose = () => onCancel();
	const handleConfirm = () => onConfirm();

	return (
		<Modal show onHide={handleClose}>
			<Modal.Header closeButton>
				<Modal.Title>Attenzione</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				Stai per procedere con l'invio dei dati anche se non hai compilato questi campi raccomandati:
				<ul>
					{fields.map((field, i) => {
						return <li key={i}>{field.label}</li>;
					})}
				</ul>
			</Modal.Body>
			<Modal.Footer>
				<Button variant='secondary' onClick={handleClose}>
					Annulla
				</Button>
				<Button variant='primary' onClick={handleConfirm}>
					Conferma
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

function FormViewerInner(props: FormViewerProps) {
	const { schema: initialSchema, onSchemaChange, onLocaleChange, onSubmit } = props;
	const { reccomended } = useContext(AppContext);
	const [schema, setSchema] = useState(initialSchema);
	const [confirmReccomended, setConfirmReccomended] = useState<Schema.Field[]>([]);
	const [submitArgs, setSubmitArgs] = useState<any>();

	useEffect(() => {
		// if updated from outside, then replace schema! (usually it happens in <FormBuilder />)
		setSchema({ ...initialSchema });
	}, [initialSchema]);

	const updateSchema = useCallback(
		(newSchema) => {
			setSchema({ ...newSchema });
			if (onSchemaChange) {
				onSchemaChange(newSchema);
			}
		},
		[onSchemaChange]
	);

	const onBeforeSubmit = useCallback(
		(values: any) => {
			const empty = reccomended.filter((field) => {
				const value = _get(values, field.name, '');
				if (value === false || isEmpty(value)) return true;
				return false;
			});
			if (empty.length === 0) return true;
			setConfirmReccomended(empty);
			return false;
		},
		[reccomended]
	);

	const submit = useCallback(
		(values, actions, bypassOnBeforeSubmit = false) => {
			if (bypassOnBeforeSubmit || onBeforeSubmit(values)) {
				if (onSubmit) {
					onSubmit({
						values,
						...props,
						form: actions,
					});
				}
			} else {
				setSubmitArgs({ values, actions });
			}
		},
		[props, onBeforeSubmit]
	);

	const drill = {
		...props,
		schema,
		initialValues: props.initialValues || {},
	};
	return (
		<ForwardedFormWithRef {...drill} onSubmit={submit}>
			{(refProps: FormProps) => {
				return (
					<FormikForm>
						<ChangesWrapper {...drill} {...refProps} onUpdateSchema={updateSchema}>
							<LocaleProvider
								localeKey={
									props.options && props.options.defaultLocaleKey
										? props.options.defaultLocaleKey
										: undefined
								}
								locales={props.options && props.options.locales ? props.options.locales : undefined}
								localizedTexts={
									props.options && props.options.localizedTexts
										? props.options.localizedTexts
										: undefined
								}
								onLocaleChange={onLocaleChange}
							>
								<Form {...drill} {...refProps} />
								{confirmReccomended && confirmReccomended.length > 0 && (
									<FormViewerReccomended
										fields={confirmReccomended}
										onConfirm={() => {
											submit(submitArgs.values, submitArgs.actions, true);
											setConfirmReccomended([]);
										}}
										onCancel={() => {
											submitArgs.actions.setSubmitting(false);
											setConfirmReccomended([]);
										}}
									/>
								)}
							</LocaleProvider>
						</ChangesWrapper>
					</FormikForm>
				);
			}}
		</ForwardedFormWithRef>
	);
}

function FormWithRef(props: any, ref: unknown) {
	const { children, onValidate, ...rest } = props;
	const formikProps = { ...rest };
	if (onValidate) {
		formikProps.validate = (values: FormikValues) => onValidate({ values });
	}
	return (
		<Formik {...formikProps}>
			{(formikProps: FormikReturnProps) => {
				if (typeof children === 'function') {
					return children({
						...props,
						form: formikProps,
					});
				}
				return children;
			}}
		</Formik>
	);
}

export const ForwardedFormWithRef = forwardRef(FormWithRef);

export default FormViewer;
